import { create } from "zustand";
import type {
  ConnectionRecord,
  GA4Property,
  GA4ReportData,
  GSCSite,
  GSCReportData,
  CSVUploadMeta,
  CSVReportData,
  GoogleAdsAccount,
  GoogleAdsReportData,
  MetaAdAccount,
  MetaAdsReportData,
} from "@agencylens/shared";

interface AppState {
  /** Stub user — spike uses a hardcoded value, replaced by magic-link post-spike. */
  user: { email: string };

  // ---- Google Analytics 4 ----
  ga4Connection: ConnectionRecord | undefined;
  ga4Properties: GA4Property[];
  selectedPropertyId: string | undefined;
  lastGa4Report: GA4ReportData | undefined;
  setGa4Connection: (rec: ConnectionRecord | undefined) => void;
  setGa4Properties: (props: GA4Property[]) => void;
  setSelectedProperty: (id: string | undefined) => void;
  setLastGa4Report: (data: GA4ReportData | undefined) => void;

  // ---- Google Search Console ----
  gscConnection: ConnectionRecord | undefined;
  gscSites: GSCSite[];
  selectedGscSiteUrl: string | undefined;
  lastGscReport: GSCReportData | undefined;
  setGscConnection: (rec: ConnectionRecord | undefined) => void;
  setGscSites: (sites: GSCSite[]) => void;
  setSelectedGscSiteUrl: (url: string | undefined) => void;
  setLastGscReport: (data: GSCReportData | undefined) => void;

  // ---- CSV upload ----
  csvUpload: CSVUploadMeta | undefined;
  csvRows: Array<Record<string, string | number>>;
  lastCsvReport: CSVReportData | undefined;
  setCsvUpload: (m: CSVUploadMeta | undefined) => void;
  setCsvRows: (rows: Array<Record<string, string | number>>) => void;
  setLastCsvReport: (d: CSVReportData | undefined) => void;

  // ---- Google Ads ----
  googleAdsConnection: ConnectionRecord | undefined;
  googleAdsAccounts: GoogleAdsAccount[];
  selectedGoogleAdsCustomerId: string | undefined;
  lastGoogleAdsReport: GoogleAdsReportData | undefined;
  setGoogleAdsConnection: (rec: ConnectionRecord | undefined) => void;
  setGoogleAdsAccounts: (accounts: GoogleAdsAccount[]) => void;
  setSelectedGoogleAdsCustomerId: (id: string | undefined) => void;
  setLastGoogleAdsReport: (data: GoogleAdsReportData | undefined) => void;

  // ---- Meta Ads ----
  metaConnection: ConnectionRecord | undefined;
  metaAccounts: MetaAdAccount[];
  selectedMetaAdAccountId: string | undefined;
  lastMetaReport: MetaAdsReportData | undefined;
  setMetaConnection: (rec: ConnectionRecord | undefined) => void;
  setMetaAccounts: (accounts: MetaAdAccount[]) => void;
  setSelectedMetaAdAccountId: (id: string | undefined) => void;
  setLastMetaReport: (data: MetaAdsReportData | undefined) => void;

  // ---- Google Drive backup ----
  driveConnection: ConnectionRecord | undefined;
  driveBackups: Array<{
    id: string;
    name: string;
    createdAt?: string;
    modifiedAt?: string;
    sizeBytes?: number;
  }>;
  setDriveConnection: (rec: ConnectionRecord | undefined) => void;
  setDriveBackups: (
    backups: Array<{
      id: string;
      name: string;
      createdAt?: string;
      modifiedAt?: string;
      sizeBytes?: number;
    }>,
  ) => void;
}

export const useApp = create<AppState>((set) => ({
  user: { email: "demo@agencylens.app" },

  ga4Connection: undefined,
  ga4Properties: [],
  selectedPropertyId: undefined,
  lastGa4Report: undefined,
  setGa4Connection: (rec) => set({ ga4Connection: rec }),
  setGa4Properties: (props) => set({ ga4Properties: props }),
  setSelectedProperty: (id) => set({ selectedPropertyId: id }),
  setLastGa4Report: (data) => set({ lastGa4Report: data }),

  gscConnection: undefined,
  gscSites: [],
  selectedGscSiteUrl: undefined,
  lastGscReport: undefined,
  setGscConnection: (rec) => set({ gscConnection: rec }),
  setGscSites: (sites) => set({ gscSites: sites }),
  setSelectedGscSiteUrl: (url) => set({ selectedGscSiteUrl: url }),
  setLastGscReport: (data) => set({ lastGscReport: data }),

  csvUpload: undefined,
  csvRows: [],
  lastCsvReport: undefined,
  setCsvUpload: (m) => set({ csvUpload: m }),
  setCsvRows: (rows) => set({ csvRows: rows }),
  setLastCsvReport: (d) => set({ lastCsvReport: d }),

  googleAdsConnection: undefined,
  googleAdsAccounts: [],
  selectedGoogleAdsCustomerId: undefined,
  lastGoogleAdsReport: undefined,
  setGoogleAdsConnection: (rec) => set({ googleAdsConnection: rec }),
  setGoogleAdsAccounts: (accounts) => set({ googleAdsAccounts: accounts }),
  setSelectedGoogleAdsCustomerId: (id) =>
    set({ selectedGoogleAdsCustomerId: id }),
  setLastGoogleAdsReport: (data) => set({ lastGoogleAdsReport: data }),

  metaConnection: undefined,
  metaAccounts: [],
  selectedMetaAdAccountId: undefined,
  lastMetaReport: undefined,
  setMetaConnection: (rec) => set({ metaConnection: rec }),
  setMetaAccounts: (accounts) => set({ metaAccounts: accounts }),
  setSelectedMetaAdAccountId: (id) => set({ selectedMetaAdAccountId: id }),
  setLastMetaReport: (data) => set({ lastMetaReport: data }),

  driveConnection: undefined,
  driveBackups: [],
  setDriveConnection: (rec) => set({ driveConnection: rec }),
  setDriveBackups: (backups) => set({ driveBackups: backups }),
}));
